/**
 * Process-wide WebSocket client for /ws/signals.
 * Lives outside React so remounts cannot close an authenticated socket.
 */

import { BACKEND_API_URL, getClerkToken } from "./apiClient";

const RECONNECT_DELAY = 3000;
const MAX_RECONNECT_DELAY = 30000;
const RECONNECT_MULTIPLIER = 1.5;
const WS_POLICY_CLOSE_CODE = 1008;
const WS_POLICY_CLOSE_BACKOFF_MS = 60_000;
const WS_TOO_MANY_CONNECTIONS_BACKOFF_MS = 120_000;
const CONNECT_DEBOUNCE_MS = 500;
const WS_AUTH_TOKEN_WAIT_MS = 9000;
const WS_AUTH_TOKEN_RETRY_MS = 250;

export type SignalWebSocketClientStatus = {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
};

export type SignalWebSocketDebugState = {
  url: string | null;
  lastClose: {
    code: number;
    reason: string;
    wasClean: boolean;
    intentional: boolean;
    at: string;
  } | null;
  lastAuthSentAt: string | null;
  lastConnectedAt: string | null;
};

type StatusListener = (status: SignalWebSocketClientStatus) => void;
type MessageListener = (message: unknown) => void;

declare global {
  interface Window {
    __tesseraSignalWs?: SignalWebSocketDebugState;
  }
}

function wsOriginFromBackendApiUrl(): string {
  if (/^https?:\/\//i.test(BACKEND_API_URL)) {
    const parsed = new URL(BACKEND_API_URL);
    const wsScheme = parsed.protocol === "https:" ? "wss:" : "ws:";
    return `${wsScheme}//${parsed.host}`;
  }

  if (typeof window !== "undefined") {
    const wsScheme = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${wsScheme}//${window.location.host}`;
  }

  return "ws://localhost:8000";
}

export function resolveSignalWebSocketUrl(websocketPath?: string | null): string {
  if (websocketPath) {
    if (/^wss?:\/\//i.test(websocketPath)) {
      return websocketPath;
    }
    const origin = wsOriginFromBackendApiUrl();
    const normalizedPath = websocketPath.startsWith("/") ? websocketPath : `/${websocketPath}`;
    return `${origin}${normalizedPath}`;
  }

  if (/^https?:\/\//i.test(BACKEND_API_URL)) {
    let wsUrl = BACKEND_API_URL.replace(/^http:\/\//, "ws://").replace(/^https:\/\//, "wss://");
    wsUrl = wsUrl.replace(/\/api\/?$/, "");
    return `${wsUrl}/ws/signals`;
  }

  return `${wsOriginFromBackendApiUrl()}/ws/signals`;
}

async function resolveAuthToken(
  getAuthToken?: (() => Promise<string | null>) | null
): Promise<string | null> {
  const fetchToken = getAuthToken ?? getClerkToken;
  const deadline = Date.now() + WS_AUTH_TOKEN_WAIT_MS;

  while (Date.now() < deadline) {
    const token = await fetchToken();
    if (token) return token;
    await new Promise((resolve) => setTimeout(resolve, WS_AUTH_TOKEN_RETRY_MS));
  }

  return null;
}

function updateDebug(patch: Partial<SignalWebSocketDebugState>): void {
  if (typeof window === "undefined") return;
  window.__tesseraSignalWs = {
    url: window.__tesseraSignalWs?.url ?? null,
    lastClose: window.__tesseraSignalWs?.lastClose ?? null,
    lastAuthSentAt: window.__tesseraSignalWs?.lastAuthSentAt ?? null,
    lastConnectedAt: window.__tesseraSignalWs?.lastConnectedAt ?? null,
    ...patch,
  };
}

class SignalWebSocketClient {
  private ws: WebSocket | null = null;
  private connectGeneration = 0;
  private intentionalDisconnect = false;
  private connectInFlight = false;
  private connectDebounceTimeout: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private reconnectDelay = RECONNECT_DELAY;
  private sessionActive = false;
  private getAuthToken: (() => Promise<string | null>) | null = null;
  private websocketPath: string | null = null;
  private statusListeners = new Set<StatusListener>();
  private messageListeners = new Set<MessageListener>();
  private status: SignalWebSocketClientStatus = {
    isConnected: false,
    isConnecting: false,
    error: null,
  };

  subscribeStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    listener(this.status);
    return () => this.statusListeners.delete(listener);
  }

  subscribeMessages(listener: MessageListener): () => void {
    this.messageListeners.add(listener);
    return () => this.messageListeners.delete(listener);
  }

  configure(options: {
    getAuthToken?: () => Promise<string | null>;
    websocketPath?: string | null;
  }): void {
    if (options.getAuthToken) {
      this.getAuthToken = options.getAuthToken;
    }
    if (options.websocketPath !== undefined) {
      this.websocketPath = options.websocketPath;
    }
  }

  /** Start / keep session — only call when Clerk is loaded and user is signed in. */
  activateSession(): void {
    this.sessionActive = true;
    if (this.connectDebounceTimeout) {
      clearTimeout(this.connectDebounceTimeout);
    }
    this.connectDebounceTimeout = setTimeout(() => {
      this.connectDebounceTimeout = null;
      if (this.ws?.readyState === WebSocket.OPEN) {
        return;
      }
      void this.connect();
    }, CONNECT_DEBOUNCE_MS);
  }

  /** End session — only call on confirmed sign-out (not during Clerk loading). */
  deactivateSession(): void {
    this.sessionActive = false;
    this.disconnect("signed-out");
  }

  reconnect(): void {
    if (!this.sessionActive) return;
    void this.connect();
  }

  private emitStatus(patch: Partial<SignalWebSocketClientStatus>): void {
    this.status = { ...this.status, ...patch };
    this.statusListeners.forEach((listener) => listener(this.status));
  }

  private emitMessage(message: unknown): void {
    this.messageListeners.forEach((listener) => {
      try {
        listener(message);
      } catch (error) {
        console.error("[signals] WS message listener failed:", error);
      }
    });
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  /** Drop a socket without triggering reconnect (superseded or signing out). */
  private retireSocket(ws: WebSocket, reason: string): void {
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    try {
      ws.close(1000, reason);
    } catch {
      // ignore
    }
  }

  private scheduleReconnect(isPolicyClose: boolean, reasonText = ""): void {
    if (!this.sessionActive) return;
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      return;
    }

    const tooMany =
      isPolicyClose &&
      (reasonText.includes("too many") || reasonText.includes("limit"));
    const delay = tooMany
      ? WS_TOO_MANY_CONNECTIONS_BACKOFF_MS
      : isPolicyClose
        ? WS_POLICY_CLOSE_BACKOFF_MS
        : this.reconnectDelay;

    this.clearReconnectTimer();
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttempts += 1;
      if (!isPolicyClose) {
        this.reconnectDelay = Math.min(this.reconnectDelay * RECONNECT_MULTIPLIER, MAX_RECONNECT_DELAY);
      }
      void this.connect();
    }, delay);
  }

  private async connect(): Promise<void> {
    if (!this.sessionActive) return;
    if (this.connectInFlight) return;

    const existing = this.ws;
    if (existing?.readyState === WebSocket.OPEN) {
      return;
    }

    this.connectInFlight = true;
    const generation = ++this.connectGeneration;

    // Retire any in-flight socket so the server does not accumulate connections.
    if (existing && existing.readyState !== WebSocket.CLOSED) {
      if (this.ws === existing) {
        this.ws = null;
      }
      this.retireSocket(existing, "replaced");
    }

    this.intentionalDisconnect = false;
    this.emitStatus({ isConnecting: true, error: null });

    const wsUrl = resolveSignalWebSocketUrl(this.websocketPath);
    updateDebug({ url: wsUrl });

    console.warn("[signals] WS connecting:", wsUrl);

    const ws = new WebSocket(wsUrl);
    this.ws = ws;

    ws.onopen = () => {
      if (generation !== this.connectGeneration || this.ws !== ws) return;

      void (async () => {
        const authToken = await resolveAuthToken(this.getAuthToken);
        if (generation !== this.connectGeneration || this.ws !== ws || !authToken) {
          console.warn("[signals] WS auth aborted before send (socket superseded or no token)");
          return;
        }

        const payload = JSON.stringify({ type: "auth", token: authToken });
        console.warn("[signals] WS sending auth", { tokenLength: authToken.length });
        updateDebug({ lastAuthSentAt: new Date().toISOString() });
        ws.send(payload);
      })();
    };

    ws.onmessage = (event: MessageEvent) => {
      if (this.ws !== ws) return;
      // Drop app events once signed out — socket close can lag wake-from-sleep /
      // Clerk expiry and would otherwise still toast performance notifications.
      if (!this.sessionActive) return;
      try {
        const message = JSON.parse(event.data);
        const type = (message as { type?: string })?.type;
        console.warn("[signals] WS message received:", { type });

        if (type === "ping" || type === "heartbeat") {
          try {
            ws.send(JSON.stringify({ type: "pong" }));
          } catch {
            // ignore
          }
          return;
        }

        if (type === "connected") {
          this.emitStatus({ isConnected: true, isConnecting: false, error: null });
          this.reconnectAttempts = 0;
          this.reconnectDelay = RECONNECT_DELAY;
          updateDebug({ lastConnectedAt: new Date().toISOString() });
          console.warn("[signals] WS authenticated and connected");
          try {
            ws.send(
              JSON.stringify({
                type: "subscribe",
                channels: [
                  "trade_request",
                  "no_signal",
                  "system_performance",
                  "account_update",
                  "trade_fpl_update",
                  "trade_fpl_updates",
                  "trade_closed",
                  "market_overview",
                ],
              })
            );
          } catch (error) {
            console.error("[signals] WS subscribe send failed:", error);
          }
        }

        this.emitMessage(message);
      } catch (error) {
        console.error("[signals] WS failed to parse message:", error);
      }
    };

    ws.onclose = (event: CloseEvent) => {
      const isCurrentSocket = this.ws === ws;
      if (isCurrentSocket) {
        this.ws = null;
      }

      // Superseded sockets must not schedule another reconnect.
      if (!isCurrentSocket) {
        return;
      }

      const closeInfo = {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
        intentional: this.intentionalDisconnect,
        at: new Date().toISOString(),
      };
      updateDebug({ lastClose: closeInfo });

      console.warn("[signals] WS closed:", closeInfo);

      this.emitStatus({ isConnected: false, isConnecting: false });

      if (this.intentionalDisconnect) {
        this.intentionalDisconnect = false;
        return;
      }

      const isPolicyClose = event.code === WS_POLICY_CLOSE_CODE;
      const reasonText = (event.reason || "").toLowerCase();
      if (isPolicyClose) {
        if (reasonText.includes("too many") || reasonText.includes("limit")) {
          this.emitStatus({
            error:
              "Too many WebSocket connections. Close other Tessera tabs, wait ~2 minutes, then refresh.",
          });
        } else if (reasonText.includes("auth")) {
          this.emitStatus({ error: "Live signals disconnected: authentication failed." });
        } else {
          this.emitStatus({
            error: "Live signals disconnected. Please refresh the page if this persists.",
          });
        }
      }

      this.scheduleReconnect(isPolicyClose, reasonText);
    };

    ws.onerror = () => {
      if (this.ws !== ws) return;
      console.error("[signals] WS error");
      this.emitStatus({ error: "WebSocket connection error", isConnecting: false });
    };

    this.connectInFlight = false;
  }

  private disconnect(reason: string): void {
    console.warn("[signals] WS disconnect requested:", reason);
    this.connectGeneration += 1;
    this.intentionalDisconnect = true;
    this.connectInFlight = false;
    this.clearReconnectTimer();
    if (this.connectDebounceTimeout) {
      clearTimeout(this.connectDebounceTimeout);
      this.connectDebounceTimeout = null;
    }

    if (this.ws) {
      const socket = this.ws;
      this.ws = null;
      this.retireSocket(socket, reason);
    }

    this.emitStatus({
      isConnected: false,
      isConnecting: false,
      error: null,
    });
    this.reconnectAttempts = 0;
    this.reconnectDelay = RECONNECT_DELAY;
  }
}

export const signalWebSocketClient = new SignalWebSocketClient();
